import { QueryHookOptions } from '@apollo/client';
import map from 'lodash/map';

import {
  TransactionDetailsQuery,
  TransactionDetailsQueryVariables,
  TransactionFilter,
  TransactionFragment,
  TransactionsQuery,
  TransactionsQueryVariables,
  useTransactionDetailsQuery,
  useTransactionsQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

interface GetTransactionsProps {
  filter?: TransactionFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<TransactionFragment[], TransactionsQueryVariables>;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
}

const transformDataToTransactionsArray = (data: TransactionsQuery): TransactionFragment[] => {
  return map(data?.transactions?.edges, 'node');
};

export const useGetTransactions = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetTransactionsProps) => {
  const response = usePaginatedQuery(
    useTransactionsQuery,
    'transactions.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToTransactionsArray(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transformDataToTransactionsArray(response.data) : response.data,
  };
};

export const useGetTransactionDetails = (
  uuidEquals: string,
  baseOptions?: QueryHookOptions<TransactionDetailsQuery, TransactionDetailsQueryVariables>,
) => {
  const response = useTransactionDetailsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        uuidEquals,
      },
    },
    ...baseOptions,
  });

  return {
    ...response,
    data: response?.data?.transactions?.edges?.[0]?.node || null,
  };
};
